/*
 * nd-event-calendar.js — liens « Ajouter à l'agenda » dérivés d'UNE date ISO
 * --------------------------------------------------------------------------
 * POURQUOI EN JS ET PAS EN LIQUID
 * Le filtre Liquid `date` formate dans le fuseau de la boutique, pas en UTC, et
 * son rendu dépend de la locale. Les fichiers .ics et l'URL Google Agenda
 * exigent un horodatage UTC strict (`20260715T160000Z`). `new Date(iso)` +
 * `toISOString()` fait la conversion correctement, quel que soit le décalage
 * écrit dans le réglage (ex. `-04:00`).
 *
 * Résultat : la date de l'événement est saisie UNE seule fois (réglage ISO), et
 * l'affichage, le JSON-LD, Google Agenda et le .ics restent forcément d'accord.
 * (La maquette codait la date en dur dans 4 sous-systèmes séparés.)
 *
 * Contrat : un conteneur [data-nd-calendar] portant
 *   data-start   : ISO 8601 avec décalage (ex. 2026-07-15T12:00:00-04:00)
 *   data-end     : ISO 8601 avec décalage
 *   data-title   : titre de l'événement
 *   data-details : description
 *   data-location: lieu
 *   data-url     : (facultatif) URL de la diffusion — voir ci-dessous
 *   data-uid     : identifiant stable du .ics
 *   data-rrule   : (facultatif) règle de récurrence RFC 5545 SANS le préfixe
 *                  « RRULE: », ex. FREQ=DAILY;COUNT=3 (rencontres de la rentrée :
 *                  3 midis consécutifs). Absente = une seule occurrence, sortie
 *                  identique à avant. Ajoutée au .ics (ligne RRULE) et à Google
 *                  Agenda (paramètre `recur`).
 * et, à l'intérieur : [data-nd-cal-google] (<a>) et [data-nd-cal-ics] (<button>).
 *
 * POURQUOI data-url
 * Un rappel d'agenda qui ne contient pas le lien de la rencontre oblige la
 * personne à retourner fouiller ses courriels à midi moins deux. On pousse donc
 * l'URL dans l'entrée d'agenda :
 *   - .ics  : propriété `URL:` du VEVENT (RFC 5545). Les clients (Apple, Outlook)
 *             l'affichent en lien cliquable. On la répète dans DESCRIPTION, car
 *             Google Agenda IGNORE `URL:` à l'import.
 *   - Google Agenda : pas de paramètre d'URL dédié — l'URL est ajoutée aux
 *             `details`, que Google transforme en lien.
 */
(function () {
  'use strict';

  function toStamp(iso) {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  }

  // RFC 5545 : échapper virgules, points-virgules et sauts de ligne.
  function escapeIcs(text) {
    return String(text || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  function init(root) {
    if (root.dataset.ndCalBound === 'true') return;
    root.dataset.ndCalBound = 'true';

    var start = toStamp(root.dataset.start);
    var end = toStamp(root.dataset.end);
    if (!start || !end) return; // date invalide : on n'affiche pas de liens cassés

    var title = root.dataset.title || '';
    var details = root.dataset.details || '';
    var location = root.dataset.location || '';
    var url = root.dataset.url || '';
    // Ligne `URL:` du .ics : seulement si la section l'active (attribut data-nd-ics-url,
    // émis quand `calendar_url` est renseigné). Sans l'attribut, le .ics reste EXACTEMENT
    // celui d'avant : le `var url` du clic masquait `url`, aucune ligne URL n'était écrite.
    var icsUrl = root.hasAttribute('data-nd-ics-url') ? url : '';
    var uid = root.dataset.uid || 'nd-' + start;
    // Récurrence : lettres, chiffres et = ; , + - seulement — jamais de saut de ligne dans le .ics.
    var rrule = (root.dataset.rrule || '').trim().replace(/^RRULE:/i, '');
    if (rrule && !/^[A-Z0-9=;,+-]+$/i.test(rrule)) rrule = '';

    // Le lien de la diffusion est joint à la description : c'est le seul champ
    // que Google Agenda ET les clients .ics affichent tous les deux.
    var detailsWithUrl = url ? (details ? details + '\n\n' + url : url) : details;

    var google = root.querySelector('[data-nd-cal-google]');
    if (google) {
      var params = new URLSearchParams({
        action: 'TEMPLATE',
        text: title,
        dates: start + '/' + end,
        details: detailsWithUrl,
        location: location
      });
      if (rrule) params.set('recur', 'RRULE:' + rrule);
      google.href = 'https://calendar.google.com/calendar/render?' + params.toString();
    }

    var icsBtn = root.querySelector('[data-nd-cal-ics]');
    if (icsBtn) {
      icsBtn.addEventListener('click', function () {
        var lines = [
          'BEGIN:VCALENDAR',
          'VERSION:2.0',
          'PRODID:-//Neurodidacte//Evenement//FR',
          'CALSCALE:GREGORIAN',
          'METHOD:PUBLISH',
          'BEGIN:VEVENT',
          'UID:' + escapeIcs(uid),
          // DTSTAMP = maintenant. La maquette codait une date fixe (20260701),
          // ce que certains clients interprètent comme la date de création.
          'DTSTAMP:' + toStamp(new Date().toISOString()),
          'DTSTART:' + start,
          'DTEND:' + end,
          'SUMMARY:' + escapeIcs(title),
          'DESCRIPTION:' + escapeIcs(detailsWithUrl),
          'LOCATION:' + escapeIcs(location),
          'END:VEVENT',
          'END:VCALENDAR'
        ];
        // `URL:` ne s'échappe pas comme du texte : les deux-points et les barres
        // obliques d'une URL sont légaux tels quels, et un `\,` casserait le lien.
        if (icsUrl) lines.splice(lines.length - 2, 0, 'URL:' + icsUrl);
        if (rrule) lines.splice(lines.indexOf('DTEND:' + end) + 1, 0, 'RRULE:' + rrule);
        var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = (root.dataset.filename || 'evenement-neurodidacte') + '.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(function () {
          URL.revokeObjectURL(url);
        }, 1500);
      });
    }
  }

  function boot() {
    document.querySelectorAll('[data-nd-calendar]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
